import { Event, useMetrics, useStore } from '@mirai/data-sources';
import { currencyFormat, useLocale } from '@mirai/locale';
import { ServiceAnna, ServiceBooking } from '@mirai/services';
import { Notification, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { ERROR } from '../../../Core.constants';
import { fetchUrlParams } from '../../../helpers';
import { getSafeTrust } from '../../../trust';
import { BookingSummary, ButtonPayment } from '../../__shared__';
import { EVENT, METRICS_EVENTS } from '../../helpers';
import { L10N } from '../Checkout.l10n';
import * as style from '../Checkout.module.css';
import { getButtonPaymentKey } from '../helpers';

const reportAnna = (diagnostic) => {
  if (window.IS_PLAYWRIGHT) return;
  try {
    Promise.resolve(ServiceAnna.track(diagnostic)).catch(() => {});
  } catch {
    return;
  }
};

const Confirmation = ({ dataSource = {}, error = false, onError, onUnconfirmed = () => {}, onValid, ...others }) => {
  const { isDesktop } = useDevice();
  const { translate } = useLocale();
  const { value: store = {} } = useStore();
  const { checkout, hotel = {}, language, locale, payment, session = {} } = store;
  const trust = getSafeTrust(store);
  const { getDistinctId } = useMetrics();
  const refContainer = useRef();

  const [busy, setBusy] = useState(false);
  const [responseError, setResponseError] = useState();
  useEffect(() => {
    !isDesktop &&
      refContainer?.current &&
      Event.publish(EVENT.FOOTER_HEIGHT_CHANGE, { height: refContainer?.current.offsetHeight });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setResponseError();
  }, [checkout, payment]);

  const { payment: { methods = {} } = {}, price: { prepayment = {} } = {} } = dataSource;
  const { currency: hotelCurrency, value } = prepayment[payment?.method] || methods[payment?.method]?.prepayment || {};

  const handleError = () => setBusy(false);

  const reportUntrusted = (assessment) => {
    if (assessment.trusted !== false) return;
    const {
      interaction = {},
      observation = {},
      authentication = {},
      history = {},
      environment = {},
      journey = {},
      navigation = {},
      validation = {},
    } = assessment.signals || {};
    try {
      const diagnostic = {
        action: 'checkout_untrusted',
        trusted: false,
        trust_score: assessment.score,
        trust_threshold: assessment.threshold,
        trust_reasons: [...assessment.reasons],
        trust_coverage: assessment.coverage,
        trust_version: assessment.version,
        trust_rules_version: assessment.rulesVersion,
        trust_attempt_id: assessment.attemptId,
        trust_assessment_id: assessment.assessmentId,
        trust_checkpoint: assessment.checkpoint,
        trust_active_ms: observation.activeMs,
        trust_journey: JSON.parse(JSON.stringify(journey)),
        trust_navigation: { ...navigation },
        trust_validation: { ...validation },
        trust_last_action_age_ms: interaction.lastActionAgeMs,
        trust_actions: interaction.trustedActions,
        trust_interaction_events: { ...interaction.events },
        trust_distinct_fields: interaction.distinctFields,
        trust_interaction_span_ms: interaction.spanMs,
        trust_activations: interaction.activations,
        trust_last_activation_age_ms: interaction.lastActivationAgeMs,
        trust_initialization_sources: { ...interaction.sources },
        trust_synthetic_events: interaction.synthetic,
        trust_session_present: authentication.sessionPresent,
        trust_login_observed: authentication.loginObserved,
        trust_previous_visit_days: history.previousVisitDays,
        trust_webdriver: environment.automated,
        trust_headless_declared: environment.headlessDeclared,
        trust_environment_anomalies: [...(environment.anomalies || [])],
        trust_browser: environment.browser,
        trust_browser_version: environment.browserVersion,
        payment_method: payment?.method,
        payment_gateway: methods[payment?.method]?.config?.id,
      };
      reportAnna({
        eventName: METRICS_EVENTS.CHECKOUT_INTERACTION,
        hotel: store.hotel?.id || store.urlParams?.idHotel || store.urlParams?.idtokenprovider || store.id,
        chain: store.type === 'chain' ? store.id : undefined,
        metadata: JSON.parse(JSON.stringify(diagnostic)),
      });
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.CHECKOUT_INTERACTION, ...diagnostic });
    } catch {
      return;
    }
  };

  const handlePress = () => {
    if (error) {
      trust.record('validationFailed');
      onError();
      return false;
    }
    const assessment = trust.check(
      { ...store, dataSource, urlParams: fetchUrlParams(location.search) },
      'beforePayment',
    );
    setBusy(true);
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.CHECKOUT_INTERACTION,
      action: 'checkout_submit',
      trusted: assessment.trusted,
      trust_score: assessment.score,
      trust_version: assessment.version,
      trust_attempt_id: assessment.attemptId,
      payment_method: payment?.method,
      payment_gateway: methods[payment?.method]?.config?.id,
    });
  };

  const handleSubmit = async (value) => {
    if (error) {
      trust.record('validationFailed');
      setBusy(false);
      return onError();
    }

    onValid();
    setResponseError();

    const nextCheckout = { ...checkout };
    const nextPayment = { ...payment, ...value };
    const currentParams = fetchUrlParams(location.search);

    const context = { ...store, checkout: nextCheckout, payment: nextPayment, urlParams: currentParams, dataSource };
    const assessment = trust.check(context, 'beforeConfirm');
    reportUntrusted(assessment);
    const response = await ServiceBooking.confirm({
      ...currentParams,
      form: nextCheckout,
      dataSource,
      language,
      payment: nextPayment,
      session: currentParams.applyClubDiscount === 'true' ? session : undefined,
      trackingUserId: getDistinctId(),
      trusted: assessment.trusted,
      trust: assessment,
    })
      .then((response) => {
        trust.observeResult(context, response);
        return response;
      })
      .catch((error) => {
        trust.observeResult(context, undefined, error);
        setBusy(false);
        setResponseError(error);

        Event.publish(EVENT.NOTIFICATION, {
          error: true,
          small: true,
          defaultMessage: translate(L10N.NOTIFICATION_ERROR[error?.code || ERROR.UNKNOWN]),
        });
        Event.publish(EVENT.METRICS, {
          id: METRICS_EVENTS.CHECKOUT_INTERACTION,
          action: 'notification_show',
          notification_type: 'error',
          notification_message: 'payment_failed',
          payment_gateway: methods[payment?.method]?.config?.id?.toLowerCase(),
        });
      });

    if (!response?.unconfirmed) return response;

    setBusy(false);
    onUnconfirmed(response);
  };

  const CTA = () => {
    const { idtokenprovider: id } = urlParams;

    return (
      <ButtonPayment
        {...dataSource?.payment}
        busy={busy}
        data-trust-action="checkout"
        data-trust-scope={trust.scope}
        hotel={{ ...hotel, id }}
        large
        promise={handleSubmit}
        wide
        onError={handleError}
        onPress={handlePress}
        testId="checkout-button"
      >
        {translate(
          getButtonPaymentKey({
            ...dataSource?.payment?.info,
            method: payment?.method,
            provider: methods[payment?.method]?.config?.id,
          }),
          {
            amount: currencyFormat({ currency: hotelCurrency, locale, value }),
          },
        )}
      </ButtonPayment>
    );
  };

  const content = () => (
    <>
      <BookingSummary dataSource={dataSource} />

      <View className={style.section}>
        {responseError && (
          <Notification error small wide>
            {translate(L10N.NOTIFICATION_ERROR[responseError?.code || ERROR.UNKNOWN])}
          </Notification>
        )}

        {CTA()}

        <Text light small wide className={style.footer}>
          {translate(L10N.NOTIFICATION_SSL_ENCRYPTION)}
        </Text>
      </View>
    </>
  );

  const urlParams = fetchUrlParams(location.search) || {};

  return (
    <View ref={refContainer} {...others} className={style.confirmation}>
      {isDesktop ? content() : CTA()}
    </View>
  );
};

Confirmation.displayName = 'Mirai:Core:Checkout:Confirmation';

Confirmation.propTypes = {
  busy: PropTypes.bool,
  dataSource: PropTypes.shape({}),
  error: PropTypes.bool,
  onError: PropTypes.func,
  onSubmit: PropTypes.func,
  onUnconfirmed: PropTypes.func,
  onValid: PropTypes.func,
};

export { Confirmation };
